import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import api from "@/lib/api";
import Seo from "@/components/Seo";
import { buildBreadcrumbs, buildFaqPage } from "@/lib/jsonLd";
import {
  ArrowUpRight,
  ShieldCheck,
  FileCheck,
  Clock,
  Hammer,
  Phone,
  Mail,
  CheckCircle2,
} from "lucide-react";

const EXPERT_FAQS = [
  {
    q: "Sous quel délai intervenez-vous pour un constat ?",
    a: "Sous 5 jours ouvrés à la réception de la mission, conformément aux exigences des principales compagnies. Visite organisée avec l'assuré, photos, métré, notes techniques.",
  },
  {
    q: "Quel délai pour le devis ?",
    a: "Sous 8 jours ouvrés après le constat, en format lisible (lignes détaillées, matériaux précisés, calendrier d'exécution).",
  },
  {
    q: "Décennale et RC Pro ?",
    a: "À jour, attestations transmises au cabinet sur simple demande — par email ou jointes au devis.",
  },
  {
    q: "Zone géographique ?",
    a: "Loire-Atlantique en priorité (Nantes, Saint-Nazaire, presqu'île de Guérande, côte de Jade). Possibilité de déplacement au-delà selon les délais.",
  },
  {
    q: "Comment fonctionne la coordination avec l'assuré ?",
    a: "Je suis l'interlocuteur direct du sinistré pendant l'exécution. Le cabinet reste informé à chaque étape clé : devis, démarrage, livraison.",
  },
];

const KPIS = [
  { value: "5j", label: "Constat sur place", sub: "Jours ouvrés max." },
  { value: "8j", label: "Devis lisible", sub: "Conforme aux grilles" },
  { value: "10", label: "Ans de décennale", sub: "Attestation sous 24h" },
  { value: "2015", label: "Artisan depuis", sub: "Compagnons du Devoir" },
];

const PROCESS = [
  {
    n: "01",
    title: "Saisine du dossier",
    body: "N° de sinistre, rapport préliminaire, coordonnées du sinistré. Reçu sous 24h, accusé renvoyé le jour même.",
  },
  {
    n: "02",
    title: "Constat sur place",
    body: "Visite dans 5 jours ouvrés. Relevés millimétriques, photos, premier dialogue avec l'occupant.",
  },
  {
    n: "03",
    title: "Devis lisible",
    body: "Chiffré sous 8 jours, conforme aux grilles. Distinction fourniture/pose, TVA, calendrier d'exécution.",
  },
  {
    n: "04",
    title: "Exécution & livraison",
    body: "Coordination calendrier, jonctions reprises à la main, remise en service propre. Cabinet informé à chaque étape clé.",
  },
];

const GARANTIES = [
  { icon: ShieldCheck, label: "Décennale à jour", sub: "Attestation transmise sans délai" },
  { icon: ShieldCheck, label: "RC Pro à jour", sub: "Attestation transmise sans délai" },
  { icon: FileCheck, label: "Devis conformes", sub: "Aux grilles des compagnies" },
  { icon: Clock, label: "Délais tenus", sub: "Sans report opportuniste" },
  { icon: Hammer, label: "Jonctions soignées", sub: "Parquet, lambris, carrelage à la main" },
];

const TYPES_SINISTRES = [
  "Dégât des eaux",
  "Incendie & fumées",
  "Effraction & vandalisme",
  "Tempête & infiltrations",
  "Bris de glace & menuiseries",
];

export default function CabinetsExpertise() {
  const [phare, setPhare] = useState(null);

  useEffect(() => {
    api
      .get("/realisations")
      .then((r) => {
        const p = r.data.find((x) => x.est_phare && x.coordination_expert) || r.data.find((x) => x.est_phare);
        setPhare(p || null);
      })
      .catch(() => {});
  }, []);

  const phareImage = phare?.photo_couverture || "https://images.unsplash.com/photo-1571786256017-aee7a0c009b6?auto=format&fit=crop&w=1600&q=80";
  const phareHref = phare ? `/realisations/${phare.slug}` : "/realisations";

  return (
    <div data-testid="cabinets-expertise-page">
      <Seo
        title="Cabinets d'expertise d'assurance — artisan référençable"
        description="Menuisier agenceur référençable pour vos dossiers de sinistre. Constat sous 5 jours, devis lisible sous 8 jours, décennale et RC Pro à jour. Coordination directe avec votre cabinet."
        path="/cabinets-d-expertise"
        jsonLd={[
          {
            "@context": "https://schema.org",
            "@type": "Service",
            name: "Menuisier agenceur — coordination cabinet d'expertise",
            provider: { "@id": "https://luigichauveau.fr/#business" },
            areaServed: { "@type": "AdministrativeArea", name: "Loire-Atlantique" },
            description:
              "Remise en état après sinistre en coordination avec cabinets d'expertise d'assurance. Constat sous 5 jours, devis sous 8 jours.",
          },
          buildFaqPage(EXPERT_FAQS),
          buildBreadcrumbs([{ name: "Cabinets d'expertise", path: "/cabinets-d-expertise" }]),
        ]}
      />

      {/* ── HERO + KPI band fusionnés en bloc sombre cohérent ── */}
      <section className="bg-foreground text-background">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10 pt-16 md:pt-20 pb-10 md:pb-12 grid md:grid-cols-12 gap-x-10 gap-y-8 items-end">
          <div className="md:col-span-7">
            <p className="text-eyebrow text-primary mb-3">Page dédiée — réseau experts</p>
            <h1 className="font-serif text-4xl md:text-5xl leading-[1.05] tracking-tight text-balance">
              Un artisan <span className="italic">pour vos dossiers</span>.
            </h1>
          </div>
          <div className="md:col-span-5 md:justify-self-end max-w-md">
            <p className="text-[14.5px] leading-relaxed text-background/80">
              Vous instruisez un dossier de sinistre. Vous cherchez un menuisier agenceur de confiance, référençable, avec décennale et RC Pro à jour, capable de tenir les délais et de produire un devis lisible.
            </p>
            <div className="mt-6 flex flex-wrap gap-3">
              <Link
                to="/contact?cible=expertise"
                className="inline-flex items-center gap-3 bg-primary text-background px-6 py-4 text-[12px] tracking-[0.16em] uppercase font-medium hover:bg-background hover:text-foreground transition-colors"
                data-testid="cabinets-cta-primary"
              >
                Confier un dossier <ArrowUpRight size={14} />
              </Link>
              <a
                href="tel:+33761051061"
                className="inline-flex items-center gap-3 border border-background/40 text-background px-6 py-4 text-[12px] tracking-[0.16em] uppercase font-medium hover:bg-background hover:text-foreground transition-colors"
              >
                07 61 05 10 61
              </a>
            </div>
          </div>
        </div>

        {/* KPI band attaché au hero — 4 chiffres clés, gold accents */}
        <div className="border-t border-background/10">
          <div className="max-w-[1400px] mx-auto px-5 md:px-10">
            <div className="grid grid-cols-2 md:grid-cols-4 divide-x divide-background/10 -mx-5 md:-mx-10">
              {KPIS.map((k, i) => (
                <div key={i} className="px-5 md:px-10 py-8 md:py-10" data-testid={`kpi-${i}`}>
                  <p className="font-serif text-5xl md:text-6xl leading-none text-[hsl(var(--gold-soft))]">{k.value}</p>
                  <p className="mt-3 text-[12px] tracking-[0.18em] uppercase font-medium">{k.label}</p>
                  <p className="mt-1 text-[12px] text-background/55">{k.sub}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── Types de sinistres couverts (chips horizontales) ── */}
      <section className="border-b border-border">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10 py-8 md:py-10 flex flex-wrap items-center gap-x-6 gap-y-3">
          <p className="text-eyebrow-gold shrink-0">Sinistres couverts</p>
          <ul className="flex flex-wrap gap-2 md:gap-3" data-testid="sinistres-list">
            {TYPES_SINISTRES.map((s) => (
              <li
                key={s}
                className="text-[11.5px] tracking-[0.14em] uppercase font-medium border border-border px-3.5 py-1.5 text-foreground/80 hover:border-primary hover:text-primary transition-colors"
              >
                {s}
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* ── PROCESS — bande dense, alternance numbered ── */}
      <section className="max-w-[1400px] mx-auto px-5 md:px-10 py-16 md:py-20">
        <div className="grid md:grid-cols-12 gap-10 mb-10 items-end">
          <div className="md:col-span-7">
            <p className="text-eyebrow-gold mb-3">01 — Process</p>
            <h2 className="font-serif text-3xl md:text-4xl leading-[1.05] tracking-tight">
              Une méthode <span className="italic text-primary">prévisible</span>.
            </h2>
          </div>
          <p className="md:col-span-5 text-[14.5px] leading-relaxed text-foreground/75 max-w-md md:justify-self-end">
            Chaque dossier suit les mêmes étapes — pour que votre instruction soit fluide, et que le sinistré récupère son cadre sans accroc.
          </p>
        </div>

        <ol className="grid md:grid-cols-4 gap-px bg-border border border-border" data-testid="cabinets-process">
          {PROCESS.map((p, i) => (
            <li
              key={p.n}
              className="bg-background p-6 md:p-7 flex flex-col gap-3 min-h-[200px] group hover:bg-muted/30 transition-colors"
              data-testid={`process-step-${i}`}
            >
              <div className="flex items-center justify-between">
                <span className="font-serif text-3xl text-primary">{p.n}</span>
                <span className="w-8 h-px bg-border group-hover:bg-primary group-hover:w-12 transition-all duration-500" />
              </div>
              <h3 className="font-serif text-xl md:text-2xl leading-tight">{p.title}</h3>
              <p className="text-[13.5px] text-muted-foreground leading-relaxed mt-auto">{p.body}</p>
            </li>
          ))}
        </ol>
      </section>

      {/* ── Garanties — bande beige dense ── */}
      <section className="bg-muted/40 border-y border-border py-16 md:py-20">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10 grid md:grid-cols-12 gap-x-10 gap-y-10">
          <div className="md:col-span-4">
            <p className="text-eyebrow-gold mb-3">02 — Garanties</p>
            <h2 className="font-serif text-3xl md:text-4xl leading-[1.05] tracking-tight mb-6">
              Tout ce que <span className="italic text-primary">vous attendez</span>.
            </h2>
            <p className="text-[14px] text-foreground/70 leading-relaxed">
              Cinq engagements, écrits noir sur blanc.
            </p>
            <div className="mt-6 p-5 border border-border bg-background flex items-start gap-3">
              <Mail size={16} className="text-primary mt-0.5 shrink-0" />
              <div>
                <p className="text-[11px] tracking-[0.18em] uppercase font-medium mb-1">Attestations</p>
                <a
                  href="mailto:contact@luigichauveau.fr"
                  className="text-[13px] text-foreground/80 link-underline-static"
                >
                  contact@luigichauveau.fr
                </a>
              </div>
            </div>
          </div>
          <ul className="md:col-span-8 grid sm:grid-cols-2 gap-px bg-border" data-testid="cabinets-garanties">
            {GARANTIES.map((g, i) => (
              <li key={i} className="bg-background p-5 md:p-6 flex items-start gap-4">
                <span className="shrink-0 w-10 h-10 rounded-full border border-primary/30 flex items-center justify-center text-primary">
                  <g.icon size={16} />
                </span>
                <div>
                  <p className="text-[14.5px] font-medium leading-tight">{g.label}</p>
                  <p className="text-[12.5px] text-muted-foreground mt-1 leading-snug">{g.sub}</p>
                </div>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* ── Cas d'école — preuve visuelle ── */}
      <section className="max-w-[1400px] mx-auto px-5 md:px-10 py-16 md:py-24">
        <div className="grid md:grid-cols-12 gap-10 items-center">
          <div className="md:col-span-7 order-2 md:order-1">
            <div className="aspect-[16/11] overflow-hidden bg-[#F5F1EA] group">
              <img
                src={phareImage}
                alt={phare?.titre || "Cas d'école — Restaurant La Promenade"}
                className="w-full h-full object-cover transition-transform duration-[1100ms] ease-out group-hover:scale-[1.03]"
                loading="lazy"
              />
            </div>
          </div>
          <div className="md:col-span-5 order-1 md:order-2">
            <p className="text-eyebrow-gold mb-3">03 — Cas d'école</p>
            <h2 className="font-serif text-3xl md:text-4xl leading-[1.05] tracking-tight mb-5">
              {phare?.titre || "Restaurant La Promenade"}
            </h2>
            <p className="text-[14.5px] text-foreground/75 leading-relaxed mb-4">
              Remise en état complète après inondation. Dossier mené en
              coordination directe avec un cabinet d'expertise d'assurance,
              dans les délais convenus et avec un calendrier compatible avec
              la reprise d'activité.
            </p>
            <ul className="space-y-2.5 mb-7">
              {[
                "Parquet contemporain large en chêne huilé",
                "Lambris cathédrale peint blanc",
                "Mur d'accent terracotta avec étagère intégrée",
                "Jonctions parquet/carrelage au millimètre",
              ].map((line) => (
                <li key={line} className="flex items-start gap-2.5 text-[13.5px] text-foreground/80">
                  <CheckCircle2 size={14} className="text-primary mt-0.5 shrink-0" />
                  <span>{line}</span>
                </li>
              ))}
            </ul>
            <Link to={phareHref} className="btn-ink inline-flex items-center gap-3" data-testid="cabinets-case-study-cta">
              Lire le dossier complet <ArrowUpRight size={14} />
            </Link>
          </div>
        </div>
      </section>

      {/* ── FAQ courte (rich snippets + réassurance) ── */}
      <section className="bg-muted/40 border-y border-border py-16 md:py-20">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10 grid md:grid-cols-12 gap-x-10 gap-y-10">
          <div className="md:col-span-4">
            <p className="text-eyebrow-gold mb-3">04 — Questions</p>
            <h2 className="font-serif text-3xl md:text-4xl leading-[1.05] tracking-tight mb-5">
              Ce qu'on me <span className="italic text-primary">demande souvent</span>.
            </h2>
            <p className="text-[14px] text-foreground/70 leading-relaxed">
              Cinq questions qui reviennent dans la prise de contact avec les cabinets.
            </p>
          </div>
          <div className="md:col-span-8 space-y-px bg-border" data-testid="cabinets-faq">
            {EXPERT_FAQS.map((f, i) => (
              <details
                key={i}
                className="bg-background p-5 md:p-6 group"
                data-testid={`faq-${i}`}
              >
                <summary className="cursor-pointer list-none flex items-start justify-between gap-4">
                  <span className="font-serif text-[17px] md:text-lg leading-snug pr-4">{f.q}</span>
                  <span className="shrink-0 mt-1 text-primary text-xl leading-none transition-transform group-open:rotate-45">+</span>
                </summary>
                <p className="mt-4 text-[13.5px] text-muted-foreground leading-relaxed">{f.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA final ── */}
      <section className="bg-foreground text-background">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10 py-16 md:py-24 grid md:grid-cols-12 gap-10 items-end">
          <div className="md:col-span-8">
            <p className="text-eyebrow text-primary mb-3">05 — Saisir mon cabinet</p>
            <h2 className="font-serif text-4xl md:text-6xl leading-[1.02] tracking-tight text-balance">
              Référencez un artisan <span className="italic text-[hsl(var(--gold-soft))]">qui tient sa parole</span>.
            </h2>
            <p className="mt-6 text-[14.5px] text-background/75 max-w-xl leading-relaxed">
              Le formulaire dédié comporte les champs métier — compagnie, numéro de sinistre, nature du sinistre — pour gagner du temps dans l'instruction.
            </p>
          </div>
          <div className="md:col-span-4 md:col-start-9 space-y-4">
            <Link
              to="/contact?cible=expertise"
              className="inline-flex items-center gap-3 bg-primary text-background px-6 py-4 text-[12px] tracking-[0.16em] uppercase font-medium hover:bg-background hover:text-foreground transition-colors w-full justify-center"
              data-testid="cabinets-cta-form"
            >
              Ouvrir le formulaire <ArrowUpRight size={14} />
            </Link>
            <a
              href="tel:+33761051061"
              className="inline-flex items-center gap-3 border border-background/40 text-background px-6 py-4 text-[12px] tracking-[0.16em] uppercase font-medium hover:bg-background hover:text-foreground transition-colors w-full justify-center"
            >
              <Phone size={13} /> 07 61 05 10 61
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
